import bytes from 'bytes';

import { Logger } from '@eluve/logger';

import { AppointmentFileIdbRow } from './files-db';
import { UserFileSystem } from './user-file-system.store';
import { extractFileStats } from './user-files-analyzer';

export type FileRow = {
  name: string;
  size: string;
  handle: FileSystemHandle;
  file: File;
};

export type AppointmentFileRow = FileRow & { appointmentId: string };

export const SWAP_FILE_EXT = '.crswap';
const NOT_FOUND_ERROR = 'NotFoundError';

/**
 * [ENG-4717] Read the byte size of a finalized OPFS file by walking the given
 * directory path from the OPFS root. Returns null if the file or any path
 * segment is missing (best-effort — used only for manifest diagnostics).
 */
export const getOpfsFileSize = async (
  dirPath: string[],
  fileName: string,
): Promise<number | null> => {
  try {
    let dir = await navigator.storage.getDirectory();
    for (const segment of dirPath) {
      dir = await dir.getDirectoryHandle(segment, { create: false });
    }
    const handle = await dir.getFileHandle(fileName, { create: false });
    return (await handle.getFile()).size;
  } catch {
    return null;
  }
};

export type AppointmentFilesResponse =
  | { type: 'fileSystemUnavailable' }
  | { type: 'directoryExists'; files: AppointmentFileRow[] }
  | { type: 'directoryNotFound' };

/**
 * Retrieves all the files related to a given appointment from the user's file system.
 */
export const readAppointmentFilesFromOpfs = async ({
  appointmentId,
  userFileSystem,
  logger,
}: {
  userFileSystem: UserFileSystem;
  appointmentId: string;
  logger?: Logger;
}): Promise<AppointmentFilesResponse> => {
  if (userFileSystem.type !== 'available') {
    return { type: 'fileSystemUnavailable' };
  }

  const { appointmentsDir } = userFileSystem;

  try {
    const appointmentDir = await appointmentsDir.getDirectoryHandle(
      appointmentId,
      { create: false },
    );

    const files: AppointmentFileRow[] = [];
    const filePromises: Promise<void>[] = [];
    for await (const [name, handle] of appointmentDir) {
      if (handle.kind === 'file') {
        filePromises.push(
          (handle as FileSystemFileHandle).getFile().then((f) => {
            const size = bytes(f.size);
            files.push({
              name,
              size,
              handle,
              file: f,
              appointmentId,
            });
          }),
        );
      }
    }

    const results = await Promise.allSettled(filePromises);

    // Log any individual file failures
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        logger?.warn(`Failed to process file at index ${index}:`, {
          reason: result.reason,
        });
      }
    });

    return { type: 'directoryExists', files };
  } catch (e) {
    if (e instanceof DOMException && e.name === NOT_FOUND_ERROR) {
      return { type: 'directoryNotFound' };
    } else {
      throw e;
    }
  }
};

/**
 * Attempts to recursively discover all files on the user's device related to appointments.
 */
export const readAllAppointmentFilesFromOpfs = async (
  fileSystem: UserFileSystem,
  logger: Logger,
  dbRecords?: AppointmentFileIdbRow[],
): Promise<AppointmentFilesResponse> => {
  if (fileSystem.type !== 'available') {
    return { type: 'fileSystemUnavailable' };
  }

  const { appointmentsDir } = fileSystem;

  const filePromises: Promise<AppointmentFileRow[]>[] = [];
  let dirCount = 0;
  const scanStart = new Date();
  for await (const [id, handle] of appointmentsDir) {
    if (handle.kind === 'directory') {
      dirCount++;
      const promise: () => Promise<AppointmentFileRow[]> = async () => {
        const appointmentFilesResult = await readAppointmentFilesFromOpfs({
          userFileSystem: fileSystem,
          appointmentId: id,
          logger,
        });

        if (appointmentFilesResult.type === 'directoryNotFound') {
          // We shouldn't get here because we found the id for this directory in the parent directory
          logger.warn(
            'Directory not found during iteration. This should not happen',
          );
        }

        if (
          appointmentFilesResult.type === 'directoryExists' &&
          appointmentFilesResult.files.length
        ) {
          return appointmentFilesResult.files;
        }

        return [];
      };

      filePromises.push(promise());
    }
  }

  const results = await Promise.allSettled(filePromises);

  const allFiles: AppointmentFileRow[] = [];
  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      allFiles.push(...result.value);
    } else {
      logger.warn(
        `Failed to process appointment directory at index ${index}:`,
        result.reason,
      );
    }
  });

  const scanEnd = new Date();

  const duration = scanEnd.getTime() - scanStart.getTime();

  const fileAnalysis =
    allFiles.length > 0 && dbRecords
      ? extractFileStats(allFiles, dbRecords, logger)
      : {};

  const logData = {
    dirCount,
    duration,
    fileCount: allFiles.length,
    fileAnalysis,
  };

  logger.info(
    `Periodic Scanned ${dirCount} directories in ${duration} ms.`,
    logData,
  );

  return {
    type: 'directoryExists',
    files: allFiles,
  };
};
